import React, { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MultiSelect } from '@/modules/common/components/molecules/selectors/multiselect';
import { getIndustries } from '@/modules/projects';
import { ProjectIndustry } from '@/modules/projects/types/project';

interface IndustrySelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Industry selection component with multi-select capability and custom industry creation.
 * Integrates with react-hook-form and supports both predefined and user-created industries.
 *
 * Industries are fetched lazily — only when the user opens the select dropdown.
 */
export function IndustrySelect<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  disabled = false,
}: IndustrySelectProps<T>) {
  const { t } = useTranslation(['common', 'authenticated.projects']);

  // Lazy-load: only fetch industries when user opens the select
  const [hasOpened, setHasOpened] = useState(false);

  const {
    data: industries = [],
    isPending: isLoadingIndustries,
    error,
  } = useQuery({
    queryKey: ['industries'],
    queryFn: getIndustries,
    staleTime: Infinity,
    enabled: hasOpened,
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && !hasOpened) {
        setHasOpened(true);
      }
    },
    [hasOpened]
  );

  // Handle error case - show empty options instead of blocking
  const options = error
    ? []
    : industries.map((industry: ProjectIndustry) => ({
        label: industry,
        value: industry,
      }));

  /**
   * Creates new custom industry option when user types non-existing industry.
   * Uses custom: prefix to differentiate from API-sourced industries.
   */
  const handleCreateOption = async (inputValue: string) => {
    return {
      label: inputValue,
      value: `custom:${inputValue}`,
    };
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <MultiSelect
            options={options}
            onValueChange={field.onChange}
            defaultValue={field.value || []}
            placeholder={
              isLoadingIndustries && hasOpened
                ? t('common:loading')
                : placeholder || t('common:select')
            }
            isSelectAllButtonVisible={false}
            isCreatable={true}
            onCreateOption={handleCreateOption}
            onOpenChange={handleOpenChange}
            disabled={disabled || (isLoadingIndustries && hasOpened)}
            className={`w-full disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-input disabled:bg-muted disabled:opacity-100`}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
